/**
 * Group-membership + group-profile actions (chat group management).
 *
 *   const { addMembers, removeMember, setAdmin, renameGroup } = useGroupActions()
 *
 * Each action round-trips through the REST API and reconciles the returned /
 * refreshed participant list into the wire store (`upsertWireConversation`),
 * which is the source of truth the member list re-renders from. Style mirrors
 * `useConversationActions` (W-series hooks).
 *
 * Backend semantics (already implemented in `messenger.api.ts`):
 *   • addMembers   → POST   /conversations/:id/members            → { participants }
 *   • removeMember → DELETE /conversations/:id/members/:userId     → 204
 *   • setAdmin     → PATCH  /conversations/:id/members/:userId     → { participants }
 *   • renameGroup  → PATCH  /conversations/:id                     → { conversation }
 *     (title / avatarUrl are admin-only server-side; group only)
 */

import { useCallback } from 'react'
import {
  messengerApi,
  MessengerApiError,
} from '../services/messenger.api'
import type {
  ConversationParticipant as WireParticipant,
} from '../services/messenger.wire'
import { useMessengerStore } from '../store/messenger.store'

interface UseGroupActions {
  /** Add members, then reflect the returned participant list in the store. */
  addMembers: (conversationId: string, userIds: string[]) => Promise<void>
  /** Remove a member, then drop them from the store participant list. */
  removeMember: (conversationId: string, userId: string) => Promise<void>
  /** Promote / demote a member, then reflect the returned participants. */
  setAdmin: (conversationId: string, userId: string, isAdmin: boolean) => Promise<void>
  /** Rename the group (+ optional avatar). `avatarUrl` undefined = leave as-is,
   *  null = clear. Reflects the returned conversation in the store. */
  renameGroup: (
    conversationId: string,
    title: string,
    avatarUrl?: string | null,
  ) => Promise<void>
}

/** Merge a fresh participant list into the stored conversation and re-upsert. */
function reconcileParticipants(conversationId: string, participants: WireParticipant[]): void {
  const store = useMessengerStore.getState()
  const conv = store.wireConversations.get(conversationId)
  if (!conv) return
  store.upsertWireConversation({ ...conv, participants })
}

export function useGroupActions(): UseGroupActions {
  const addMembers = useCallback(async (conversationId: string, userIds: string[]) => {
    if (userIds.length === 0) return
    const { participants } = await messengerApi.addMembers(conversationId, userIds)
    reconcileParticipants(conversationId, participants)
  }, [])

  const removeMember = useCallback(async (conversationId: string, userId: string) => {
    await messengerApi.removeMember(conversationId, userId)
    const store = useMessengerStore.getState()
    const conv = store.wireConversations.get(conversationId)
    if (!conv?.participants) return
    store.upsertWireConversation({
      ...conv,
      participants: conv.participants.filter((p) => p.userId !== userId),
    })
  }, [])

  const setAdmin = useCallback(async (conversationId: string, userId: string, isAdmin: boolean) => {
    const { participants } = await messengerApi.setMemberAdmin(conversationId, userId, isAdmin)
    reconcileParticipants(conversationId, participants)
  }, [])

  const renameGroup = useCallback(async (
    conversationId: string,
    title: string,
    avatarUrl?: string | null,
  ) => {
    const patch: { title: string; avatarUrl?: string | null } = { title }
    if (avatarUrl !== undefined) patch.avatarUrl = avatarUrl
    const { conversation } = await messengerApi.patchConversation(conversationId, patch)
    const store = useMessengerStore.getState()
    const existing = store.wireConversations.get(conversationId)
    // PATCH returns the conversation WITHOUT hydrated participants (detail GET
    // only) — preserve the ones already in the store so the member list stays.
    store.upsertWireConversation({
      ...conversation,
      participants: conversation.participants ?? existing?.participants,
    })
  }, [])

  return { addMembers, removeMember, setAdmin, renameGroup }
}

export { MessengerApiError }
